// CONTRACT:C2-RENDER-ENGINE.1.0
/** ZOOM-DRIVEN DECLUTTER plan — the pure decision logic for the "zoom-out declutter"
 *  presentation (## Behavioral Contracts: "LOD + GPU aggregation by zoom").
 *
 *  WHAT: as the operator zooms OUT, discrete markers overlap into an unreadable thicket.
 *  This computes, from the active zoom + entity count, (a) a marker SIZE multiplier that
 *  shrinks markers toward a floor as you zoom out, (b) whether to switch to an AGGREGATED
 *  "colour-cloud" presentation (deferring to the H3 decision in lod.ts for the dense case),
 *  and (c) whether per-dot LABELS (callsigns) should draw at all (they are pure noise once
 *  dots collide). The backend (apps/cop/src/host/deckBackend.ts) reads markerScale into the
 *  marker getSize and gates the callsign TextLayer on labelsVisible.
 *
 *  VISIBILITY LAW (I13): declutter is presentation ONLY. Shrinking / aggregating / hiding a
 *  LABEL never changes WHAT the projection renders — every entity remains present + pickable;
 *  only its on-screen size/label changes. It can never reveal a denied track nor hide a
 *  permitted one.
 *
 *  PURE: imports nothing, deterministic, no GPU. Tested in packages/render-engine/test/
 *  declutter.test.ts (the updateTrigger flip/noop contract — markerScale is bucketed so a
 *  sub-step zoom nudge is a NO-OP). */

/** Inputs to {@link declutterPlan}. */
export interface DeclutterInput {
  /** Active MapLibre zoom (0..22). */
  zoom: number;
  /** Number of entities competing for screen space in this layer. */
  entityCount: number;
  /** At/above this zoom markers are full-size (scale 1). Default 9. */
  fullDetailZoom?: number;
  /** At/below this zoom markers are smallest (scale = minScale). Default 2. */
  minDetailZoom?: number;
  /** The smallest marker-size multiplier (zoomed all the way out). Default 0.55. */
  minScale?: number;
  /** At/below this zoom per-dot labels are suppressed (overlap noise). Default 7. */
  labelMinZoom?: number;
  /** Aggregate to a colour-cloud when count exceeds this AND zoomed out. Default 2000. */
  aggregateOverCount?: number;
}

/** The declutter decision for one layer at the current view. */
export interface DeclutterPlan {
  /** Marker-size multiplier in [minScale, 1], quantized to 0.05 steps (trigger-stable). */
  markerScale: number;
  /** Whether to present as an aggregated colour-cloud rather than discrete markers. */
  aggregate: boolean;
  /** Whether per-dot labels (callsigns) should draw at this zoom. */
  labelsVisible: boolean;
}

const DEFAULTS = {
  fullDetailZoom: 9,
  minDetailZoom: 2,
  minScale: 0.55,
  labelMinZoom: 7,
  aggregateOverCount: 2000,
} as const;

function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/** Quantize to a step so the trigger key is stable within a bucket (a sub-step zoom nudge is
 *  a NO-OP — the deck layer does not rebuild its size buffer for an imperceptible change). */
function quantize(x: number, step: number): number {
  return Math.round(x / step) * step;
}

/** Decide the declutter plan for a layer. PURE + total (clamped, never NaN):
 *  - markerScale ramps linearly from `minScale` (at/below minDetailZoom) to 1 (at/above
 *    fullDetailZoom), quantized to 0.05 so the updateTrigger only flips across buckets.
 *  - aggregate when the set is dense (> aggregateOverCount) AND below fullDetailZoom.
 *  - labelsVisible only at/above labelMinZoom. */
export function declutterPlan(input: DeclutterInput): DeclutterPlan {
  const zoom = Number.isFinite(input.zoom) ? input.zoom : DEFAULTS.fullDetailZoom;
  const count = Number.isFinite(input.entityCount) ? Math.max(0, input.entityCount) : 0;
  const full = input.fullDetailZoom ?? DEFAULTS.fullDetailZoom;
  const minZ = input.minDetailZoom ?? DEFAULTS.minDetailZoom;
  const minScale = input.minScale ?? DEFAULTS.minScale;
  const labelMinZoom = input.labelMinZoom ?? DEFAULTS.labelMinZoom;
  const aggregateOver = input.aggregateOverCount ?? DEFAULTS.aggregateOverCount;

  // Linear ramp minScale..1 across [minZ, full].
  const span = full - minZ;
  const t = span > 0 ? clamp((zoom - minZ) / span, 0, 1) : 1;
  const rawScale = minScale + (1 - minScale) * t;
  const markerScale = clamp(quantize(rawScale, 0.05), minScale, 1);

  return {
    markerScale,
    aggregate: count > aggregateOver && zoom < full,
    labelsVisible: zoom >= labelMinZoom,
  };
}

/** A stable string fingerprint of a {@link DeclutterPlan} for deck.gl `updateTriggers`.
 *  FLIPS when the declutter state changes (a new size bucket / aggregate flip / label flip)
 *  and is a NO-OP otherwise — so a continuous zoom gesture only re-keys the marker buffer at
 *  bucket boundaries, never on every rAF. */
export function declutterTriggerKey(plan: DeclutterPlan): string {
  return `${plan.markerScale.toFixed(2)}|${plan.aggregate ? 1 : 0}|${plan.labelsVisible ? 1 : 0}`;
}
